import { useCallback, useEffect, useState } from 'react';
import { playReload } from '@components/sound/Sound';
import { useGameStore, useAppStore } from '@stores';
import { MessageEmitter } from '@phaser/events';
import * as Shared from '@shared/lib';

interface AmmoIndicatorProps {
  onProgress: (progress: number) => void;
};

export function AmmoIndicator({ onProgress }: AmmoIndicatorProps) {
  const [ammo, setAmmo] = useState(-1);

  const { weaponStats } = useGameStore();

  const reloadDuration = weaponStats?.reloadDuration ?? 1000;
  const maxAmmo = weaponStats?.clipSize ?? 0;

  const onPlayerShoot = (data: Shared.PlayerShootMessage) => {
    const id = useAppStore.getState().id;

    if (!id.startsWith(data.playerId)) return;
    if (data.remainingBullets < 0) return;

    setAmmo(data.remainingBullets);
  };

  const startReload = useCallback((force = false) => {
    if ((ammo > 0 && !force) || !maxAmmo) return;

    playReload();

    setAmmo(0);
    const start = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(1, elapsed / reloadDuration);

      onProgress(progress);

      if (progress >= 1) {
        setAmmo(maxAmmo);
        onProgress(0);
        clearInterval(interval);
      }
    }, 20);

    return () => clearInterval(interval);
  }, [ammo, maxAmmo, reloadDuration]);

  useEffect(() => startReload(), [ammo]);
  useEffect(() => setAmmo(maxAmmo), [maxAmmo]);
  useEffect(() => { MessageEmitter.emit('ammoUpdate', ammo, maxAmmo); }, [ammo, maxAmmo]);

  useEffect(() => {
    Shared.MessageRegistry.emitter.on(Shared.PlayerShootMessage.Opcode, onPlayerShoot);
    MessageEmitter.on('reload', startReload);
    return () => {
      Shared.MessageRegistry.emitter.off(Shared.PlayerShootMessage.Opcode, onPlayerShoot);
      MessageEmitter.off('reload', startReload);
    };
  }, []);

  return (
    <div className="flex pr-2">
      <div className="flex flex-col items-center text-mono">
        <p className="leading-none">{formatAmmo(ammo)}</p>
        <p className="text-gray-500 leading-none mb-[-2px]">{formatAmmo(maxAmmo)}</p>
      </div>
    </div>
  );
}

function formatAmmo(ammo: number) {
  if (ammo < 0) return '--';
  if (ammo < 10) return `0${ammo}`;
  return ammo;
}